import { useRef, useState } from "react";
import { Paperclip, FileArrowUp, FileText, Image as ImageIcon, X, DownloadSimple } from "@phosphor-icons/react";
import { http, formatApiError, API } from "../lib/api";
import { shortenFileName } from "../lib/utils";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const IMG_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg"]);
const PDF_EXTS = new Set(["pdf"]);

function bytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function IconFor({ ext }) {
  const e = (ext || "").toLowerCase();
  if (IMG_EXTS.has(e)) return <ImageIcon size={14} weight="bold" className="text-blue-600" />;
  if (PDF_EXTS.has(e)) return <FileText size={14} weight="bold" className="text-red-600" />;
  return <FileText size={14} weight="bold" className="text-zinc-500" />;
}

export default function TaskAttachments({ taskId, files, canEdit, canDownload, onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const list = files || [];
  const remaining = 5 - list.length;

  const pick = () => inputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await http.post(`/tasks/${taskId}/references`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Reference uploaded");
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const askRemove = (file) => setPendingDelete(file);

  const confirmRemove = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await http.delete(`/tasks/${taskId}/references/${pendingDelete.id}`);
      toast.success("Reference removed");
      setPendingDelete(null);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const downloadUrl = (fileId) => `${API}/tasks/${taskId}/references/${fileId}/download`;

  if (!canEdit && list.length === 0) return null;

  return (
    <div className="mt-3 border-t border-zinc-200 pt-3" data-testid={`task-attachments-${taskId}`}>
      <div className="flex items-center gap-2 mb-2">
        <Paperclip size={12} weight="bold" className="text-zinc-500" />
        <span className="label-overline text-zinc-500">References {list.length}/5</span>
        {canEdit && remaining > 0 && (
          <button
            type="button" onClick={pick} disabled={uploading}
            data-testid={`upload-ref-${taskId}`}
            className="ml-auto label-overline flex items-center gap-1 text-blue-700 hover:text-blue-900 disabled:opacity-50"
          >
            <FileArrowUp size={12} weight="bold" />
            {uploading ? "Uploading…" : "Add"}
          </button>
        )}
        <input ref={inputRef} type="file" hidden onChange={upload} />
      </div>
      <div className="space-y-1.5">
        {list.map((f) => (
          <div key={f.id} data-testid={`ref-${f.id}`}
               className="flex items-center gap-2 border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-xs">
            <IconFor ext={f.ext} />
            <span className="flex-1 min-w-0 truncate font-mono text-zinc-800" title={f.name}>
              {shortenFileName(f.name, 32)}
            </span>
            <span className="text-zinc-500 font-mono shrink-0">{bytes(f.size || 0)}</span>
            {canDownload && (
              <a href={downloadUrl(f.id)} target="_blank" rel="noopener noreferrer"
                 className="p-1 text-zinc-600 hover:text-blue-600" aria-label="Download">
                <DownloadSimple size={13} weight="bold" />
              </a>
            )}
            {canEdit && (
              <button type="button" onClick={() => askRemove(f)} className="p-1 text-zinc-400 hover:text-red-600" aria-label="Remove"
                      data-testid={`delete-ref-${f.id}`}>
                <X size={13} weight="bold" />
              </button>
            )}
          </div>
        ))}
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-red-600 mb-1">Destructive Action</div>
            <AlertDialogTitle className="font-display text-xl font-black tracking-tighter uppercase">
              Remove reference?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              Delete <strong className="break-all">&quot;{pendingDelete?.name}&quot;</strong> from this task&apos;s references. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-delete-ref"
              className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100"
            >Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-ref"
              disabled={deleting}
              onClick={confirmRemove}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs"
            >{deleting ? "Removing…" : "Yes, Remove"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
